import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import ExoForm from '../exo/ExoForm';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import { useEffect, useState } from 'react';
import Feedback from '../special/Feedback';
import * as Yup from 'yup';
import DeleteIcon from '@mui/icons-material/Delete';

export const NewEmailIntegration = ({
  onChange,
  onDelete,
  userId,
  startData,
  onClose,
  debug,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [isLoading, setIsLoading] = useState(false);

  const fields = [
    {
      label: t('Color'),
      key: 'color',
      type: 'color',
      colspan: 3,
    },

    {
      label: t('Label'),
      key: 'label',
      type: 'text',
      colspan: 3,
    },
    {
      label: t('Server'),
      key: 'imapHost',
      type: 'text',
      before: () => (
        <Typography fontWeight={600} className="h-[10px] col-span-3">
          {t('IMAP')}
        </Typography>
      ),
      colspan: 2,
    },
    {
      label: t('Port'),
      key: 'imapPort',
      type: 'number',
      unformatted: true,
      colspan: 1,
    },
    {
      label: t('Username'),
      key: 'imapUsername',
      type: 'text',
      colspan: 3,
    },
    {
      label: t('Password'),
      key: 'imapPassword',
      type: 'password',
      colspan: 2,
    },
    {
      label: t('Encryption'),
      key: 'imapEncryption',
      type: 'customSelect',
      options: [
        {
          label: 'SSL',
          value: 'ssl',
        },
        {
          label: 'TLS',
          value: 'tls',
        },
        {
          label: 'STARTTLS',
          value: 'starttls',
        },
        {
          label: 'NOTLS',
          value: 'notls',
        },
        {
          label: t('No Encryption'),
          value: '',
        },
      ],
    },
    {
      label: t('Server'),
      key: 'smtpHost',
      type: 'text',
      before: () => (
        <Typography fontWeight={600} className="h-[10px] col-span-3">
          {t('SMTP')}
        </Typography>
      ),
      colspan: 2,
    },
    {
      label: t('Port'),
      key: 'smtpPort',
      type: 'number',
      unformatted: true,
      colspan: 1,
    },
    {
      label: t('Username'),
      key: 'smtpUsername',
      type: 'text',
      colspan: 3,
    },
    {
      label: t('Password'),
      key: 'smtpPassword',
      type: 'password',
      colspan: 2,
    },
    {
      label: t('Encryption'),
      key: 'smtpEncryption',
      type: 'customSelect',
      options: [
        {
          label: 'SSL',
          value: 'ssl',
        },
        {
          label: 'TLS',
          value: 'tls',
        },
        {
          label: 'STARTTLS',
          value: 'starttls',
        },
        {
          label: 'NOTLS',
          value: 'notls',
        },
        {
          label: t('No Encryption'),
          value: '',
        },
      ],
    },
  ];

  function handleSubmit(data) {
    const isUpdate = Boolean(data.id);

    const method = isUpdate
      ? API_PATCH({ ...data, userId })
      : API_POST({ ...data, userId });

    DBRequest({
      config,
      path: `email-credentials`,
      method,
      onResponse: (res) => {
        onClose();
        onChange(res);
      },
      onFeedback: handleFeedback,
      onLoading: setIsLoading,
    });
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const validationSchema = Yup.object().shape({
    color: Yup.string().required(t('Is required')),
    label: Yup.string().required(t('Is required')),
    imapHost: Yup.string().required(t('Is required')),
    imapPort: Yup.string().required(t('Is required')),
    imapUsername: Yup.string().required(t('Is required')),
    imapPassword: Yup.string().required(t('Is required')),
    imapEncryption: Yup.string().required(t('Is required')),
    smtpHost: Yup.string().required(t('Is required')),
    smtpPort: Yup.string().required(t('Is required')),
    smtpUsername: Yup.string().required(t('Is required')),
    smtpPassword: Yup.string().required(t('Is required')),
    smtpEncryption: Yup.string().required(t('Is required')),
  });

  const defaultData = {
    imapPort: 993,
    imapEncryption: 'ssl',
    smtpPort: 587,
    smtpEncryption: 'tls',
    color: '#339e87',
  };

  function handleDelete() {
    // Handle delete logic here
    DBRequest({
      config,
      path: `email-credentials/${startData.id}`,
      method: API_DELETE,
      onResponse: () => {
        onDelete(startData);
        onClose();
      },
      onLoading: setIsLoading,
    });
  }

  const [defaultStartData, setDefaultStartData] = useState(defaultData);
  useEffect(() => {
    if (startData) setDefaultStartData(startData);
  }, [startData]);

  return (
    <>
      {startData && startData.id && (
        <IconButton
          onClick={handleDelete}
          sx={{ position: 'absolute' }}
          className="top-2 right-2 z-10"
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      )}
      <ExoForm
        debug={debug}
        header={t('Add E-Mail Account')}
        className="grid grid-cols-3 gap-4"
        fields={fields}
        onSubmit={handleSubmit}
        startDataCollection={defaultStartData}
        size="small"
        validationSchema={validationSchema}
        noAutoReset
        noDifferences
        onCancel={onClose}
        isLoading={isLoading}
      />
      <Feedback state={alertState} setState={setAlertState} />
    </>
  );
};
